import arriereBackoffice, {
  AirbnbFeeType,
  BookingConnection,
  BookingLeadSource,
  BookingPeriodReference,
  BookingStatus,
  DocumentRequest,
  Pagination,
  PaginationInput,
  ServiceRequestStatusFilter,
  Status,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import arrierePublic from '@avantstay/arriere-clients/dist/arrierePublic'

import {
  AlterAirbnbBookingProps,
  AlterBookingBasicParams,
  AlterBookingVariables,
  IUpdateBookingParams,
  PreviewAlterDatesVariables,
  PreviewPetCountAlterationQuoteVariables,
} from '@new/domains/bookings/_types'
import type { CancelReservationActionProps } from '@new/domains/bookings/CancelBooking/CancelBooking.types'
import type {
  GetBookingConflictsParams,
  UpdateBookingConflictNote,
} from '@new/domains/bookings/screens/bookingConflicts/BookingConflicts.types'
import type {
  AlterBookingDetailsMutation,
  BookingHomeReadyForCheckIn,
  UpdateBookingHomeReadyForCheckIn,
} from '@new/domains/bookings/screens/bookingInfo/BookingInfo.types'
import type { GetBookingMessagesProps } from '@new/domains/bookings/screens/bookingMessages/BookingMessages.types'
import type { BookingFolioChangeHistory } from '@new/domains/bookings/screens/bookingPayment/actionPanels/ItemHistory/ItemHistory.types'
import type {
  BookingAccountingResolutionCenterFilterParams,
  CancelBookingWithRefundsProps,
} from '@new/domains/bookings/stores/_types'
import { API_MAX_USAGE_INFINITY_VALUE } from '@new/domains/bookingsTapeChart/BookingsTapeChart.constants'
import type { BookingsForTapeChartProps } from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'

import type { RefundMethodView } from '@arriere/types/BookingAdjustment.types'
import { BookingInfoArgs, GetPendingAirbnbAlterationsProps } from '@arriere/types/BookingInfo.types'

export default {
  createRentalAgreement(bookingId: string) {
    return arriereBackoffice.mutations.contract({
      createRentalAgreement: { __args: { bookingId }, id: true },
    })
  },
  resendRentalAgreement(contractId: string) {
    return arriereBackoffice.mutations.contract({
      resendRentalAgreement: { __args: { contractId }, id: true },
    })
  },
  async getBookingCases(bookingId: string) {
    return arriereBackoffice.queries
      .cases({
        __alias: 'getBookingCases',
        bookingCases: {
          __args: { bookingId },
          cases: {
            workOrderId: true,
            taskType: true,
            subject: true,
            status: true,
            priority: true,
            caseOwner: {
              name: true,
              email: true,
              phone: true,
            },
            inStayGuestIssue: true,
            dueDate: true,
            createdAt: true,
            createdBy: {
              name: true,
            },
            caseNumber: true,
            caseSfdcId: true,
          },
          workOrders: {
            id: true,
            workOrderType: true,
            taskType: true,
            sfdcStatusApiName: true,
            priority: true,
            vendor: {
              name: true,
            },
            workers: {
              fullName: true,
              phone: true,
            },
            inStayGuestIssue: true,
            stats: {
              dueDate: true,
            },
            createdAt: true,
            sfdcCaseNumber: true,
            caseSfdcId: true,
          },
        },
      })
      .then(results => {
        return results.bookingCases
      })
      .catch(errors => {
        throw Error(errors[0]?.message)
        return undefined
      })
  },
  async bookingContracts(bookingId: string) {
    return arriereBackoffice.queries.booking({
      __alias: 'getBookingContracts',
      __args: { bookingId },
      id: true,
      emails: true,
      contracts: {
        id: true,
        bookingId: true,
        additionalData: true,
        rentalAgreement: true,
        status: true,
        createdAt: true,
        signerEmail: true,
        signingUrl: true,
      },
    })
  },

  cancelRentalAgreement(contractId: string) {
    return arriereBackoffice.mutations.contract({
      cancelRentalAgreement: {
        __args: { contractId },
        id: true,
      },
    })
  },

  async getBookingBalanceSummary(bookingId: string) {
    return arriereBackoffice.queries
      .booking({
        __alias: 'getBookingBalanceSummary',
        __args: {
          bookingId,
        },
        folio: {
          summary: {
            balance: true,
            deposit: {
              balance: true,
            },
            deposits: { balance: true, total: true, paid: true },
            isPaid: true,
            paid: {
              total: true,
              credits: true,
            },
            paymentBalanceStatus: true,
            total: true,
            value: true,
            valueNightsAndFees: true,
          },
        },
      })
      .then(response => response?.folio.summary)
  },

  async searchTermBookings({
    filter,
    pagination,
  }: {
    filter?: string
    pagination?: PaginationInput
  }) {
    return arriereBackoffice.queries.bookings({
      __alias: 'searchTermBookings',
      __args: {
        filter,
        pagination,
      },
      pages: true,
      results: {
        id: true,
        status: true,
        hash: true,
        period: {
          start: true,
          end: true,
        },
        detailedCheckIn: {
          effectiveTime: true,
        },
        detailedCheckOut: {
          effectiveTime: true,
        },
        source: true,
        guestName: true,
        guests: true,
        home: {
          id: true,
          name: true,
          links: {
            listings: {
              leadSource: true,
              url: true,
            },
            manual: true,
          },
          region: {
            name: true,
          },
        },
      },
    })
  },

  async getBookingsForTapeChart({
    filter,
    from,
    to,
    regionIds,
    homeIds,
    status,
    securityDepositOnHold,
  }: BookingsForTapeChartProps) {
    return arriereBackoffice.queries.bookings({
      __alias: 'getBookingsForTapeChart',
      __args: {
        filter,
        from,
        to,
        regionIds,
        homeIds,
        status: status || [
          BookingStatus.booked,
          BookingStatus.checkedIn,
          BookingStatus.checkedOut,
          BookingStatus.modified,
          BookingStatus.noShow,
          BookingStatus.requested,
        ],
        securityDepositOnHold,
        periodReference: BookingPeriodReference.period,
        pagination: { page: 1, size: API_MAX_USAGE_INFINITY_VALUE },
      },
      results: {
        id: true,
        activeServicesCount: true,
        folio: {
          summary: {
            total: true,
            paid: {
              total: true,
            },
            paymentBalanceStatus: true,
          },
        },
        source: true,
        hash: true,
        homeId: true,
        guestName: true,
        securityDepositStatus: true,
        period: {
          start: true,
          end: true,
        },
        home: {
          links: {
            listings: {
              leadSource: true,
              url: true,
            },
            manual: true,
          },
        },
        notes: true,
        issues: true,
        checkIn: true,
        checkOut: true,
        detailedCheckIn: {
          custom: {
            time: true,
          },
        },
        detailedCheckOut: {
          custom: {
            time: true,
          },
        },
        customFields: {
          __args: { tags: [] },
          fields: {
            id: true,
            value: true,
          },
        },
      },
    })
  },

  async getBookingById(bookingId?: string) {
    return bookingId === undefined
      ? undefined
      : arriereBackoffice.queries.booking
          .raw({
            __alias: 'getBookingById',
            __args: {
              bookingId,
            },
            aboutUsDetails: true,
            acquisition: {
              acquisitionPhase: true,
              acquisitionType: true,
              importedOn: true,
              isAcquisition: true,
              partner: true,
              partnerBookingId: true,
            },
            address: {
              address: true,
              city: true,
              regionId: true,
              zipCode: true,
            },
            appDownloads: true,
            attachments: {
              name: true,
              url: true,
            },
            bookingGroupName: true,
            cancellationInfo: {
              appliedCancellationPolicy: {
                cancellationPolicyId: true,
              },
              author: {
                kind: true,
                name: true,
              },
              createDate: true,
              note: true,
              reason: true,
              source: true,
              stayCycle: true,
            },
            checkInType: true,
            checkInInstructionsLastSent: true,
            confirmationId: true,
            groupBooking: { id: true, name: true },
            contracts: {
              additionalData: true,
              bookingId: true,
              createdAt: true,
              detailsUrl: true,
              filesUrl: true,
              id: true,
              rentalAgreement: true,
              signingUrl: true,
              status: true,
            },
            couponCode: true,
            createdAt: true,
            customFields: {
              __args: { tags: [] },
              fields: {
                assets: {
                  createdAt: true,
                  fileName: true,
                  id: true,
                  size: true,
                  url: true,
                },
                id: true,
                kind: true,
                name: true,
                options: true,
                requiredPrivileges: {
                  action: true,
                  category: true,
                  name: true,
                },
                restriction: true,
                userAllowedActions: true,
                value: true,
              },
              section: {
                id: true,
                name: true,
                readOnly: true,
              },
            },
            detailedCheckIn: {
              custom: {
                status: true,
                time: true,
              },
              effectiveTime: true,
              originalTime: true,
            },
            detailedCheckOut: {
              custom: {
                status: true,
                time: true,
              },
              effectiveTime: true,
              originalTime: true,
            },
            emails: true,
            folio: {
              items: {
                adjustment: {
                  amount: true,
                  reason: true,
                },
                balance: true,
                cancellation: {
                  reason: true,
                  time: true,
                },
                createdAt: true,
                date: true,
                details: {
                  date: true,
                  dateRange: {
                    end: true,
                    start: true,
                  },
                  dateTime: true,
                  items: true,
                  quantity: true,
                  serviceRequestId: true,
                  time: true,
                },
                id: true,
                kind: true,
                note: true,
                title: true,
                total: true,
              },
              summary: {
                balance: true,
                deposit: {
                  balance: true,
                },
                isPaid: true,
                paid: {
                  credits: true,
                  total: true,
                },
                paymentBalanceStatus: true,
                total: true,
                value: true,
                valueNightsAndFees: true,
              },
            },
            guestId: true,
            guestList: {
              credits: true,
              email: true,
              name: true,
              personId: true,
              phone: true,
              roomId: true,
              tags: {
                tag: {
                  id: true,
                  name: true,
                  vipLevel: true,
                },
              },
            },
            guestName: true,
            guests: true,
            hash: true,
            heardAboutUs: true,
            homeId: true,
            hotel: {
              name: true,
              id: true,
            },
            hotelRoomType: {
              hotelId: true,
              id: true,
              roomKind: true,
              title: true,
            },
            id: true,
            idVerificationState: {
              passed: true,
              required: true,
            },
            isFromHotel: true,
            isRoomAssigned: true,
            issues: true,
            links: {
              manual: true,
              reservationHub: true,
              zendeskLink: true,
            },
            lockCode: {
              code: true,
            },
            lockStatus: {
              name: true,
              connectivity: true,
              offlineSince: true,
              updatedAt: true,
              batteryLevel: true,
              batteryStatus: true,
            },
            marketingInfo: {
              adults: true,
              children: true,
              infants: true,
              notes: true,
              purposeOfStay: true,
              trafficSource: true,
            },
            notes: true,
            otaBookingInfo: {
              channel: true,
              channelAccount: {
                channelId: true,
                name: true,
              },
              channelBookingId: true,
              ota: true,
              otaBookingId: true,
              listingPlatform: true,
            },
            paymentPlan: {
              createdAt: true,
              createdBy: {
                name: true,
              },
              updatedAt: true,
              updatedBy: {
                name: true,
              },
              value: true,
            },
            period: {
              end: true,
              start: true,
            },
            periodChanges: true,
            pets: true,
            phones: true,
            protection: {
              orderId: true,
              price: true,
              status: true,
              time: true,
            },
            reservationId: true,
            relocations: true,
            reservationType: {
              externalName: true,
              id: true,
              isOwnerRelated: true,
              isRevenueGenerating: true,
              name: true,
            },
            services: {
              requests: {
                __args: { status: ServiceRequestStatusFilter.all },
                data: {
                  date: true,
                  dates: true,
                  dateTime: true,
                  name: true,
                  quantity: true,
                },
                expiresAt: true,
                id: true,
                name: true,
                paymentInfo: {
                  amount: true,
                  status: true,
                },
                status: true,
              },
            },
            source: true,
            status: true,
            tags: {
              bookingTags: {
                short: true,
                full: {
                  reason: true,
                  tag: {
                    name: true,
                    description: true,
                    hierarchy: true,
                  },
                },
              },
              guestsTags: {
                short: true,
                full: {
                  reason: true,
                  tag: {
                    name: true,
                    description: true,
                    hierarchy: true,
                  },
                },
              },
            },
            updatedAt: true,
            userId: true,
          })
          .then(async ({ data, errors }) => {
            if (errors?.length) {
              throw new Error(errors[errors?.length - 1]?.message)
            }

            const home = await getBookingHomeInfo({ bookingId })

            return { ...data, home }
          })
  },

  async getBookingItemHistory(
    bookingId: string,
    itemId: string,
  ): Promise<BookingFolioChangeHistory[]> {
    return arriereBackoffice.queries
      .booking({
        __alias: 'getBookingItemHistory',
        __args: {
          bookingId,
        },
        folio: {
          changes: {
            __args: { itemId },
            id: true,
            itemId: true,
            kind: true,
            item: {
              id: true,
              kind: true,
              title: true,
              total: true,
              createdAt: true,
            },
            adjustment: {
              amount: true,
              reason: true,
            },
            cancellation: {
              time: true,
              reason: true,
            },
            refund: {
              total: true,
              reason: true,
            },
            author: {
              kind: true,
              name: true,
            },
            time: true,
            balance: true,
          },
        },
      })
      .then(response => response?.folio?.changes)
  },

  async getBookingHomeReadyForCheckIn(
    bookingId: string,
  ): Promise<BookingHomeReadyForCheckIn | undefined> {
    return arriereBackoffice.queries
      .booking({
        __alias: 'getBookingHomeReadyForCheckIn',
        __args: {
          bookingId,
        },
        homeReadyForCheckInStatus: {
          author: true,
          managedUnitId: true,
          status: true,
          reason: true,
          note: true,
          workOrderId: true,
          createdAt: true,
          relatedCase: {
            workOrderId: true,
            subject: true,
            casePriority: true,
            dueDate: true,
          },
        },
      })
      .then(home => home.homeReadyForCheckInStatus)
  },

  async updateBookingHomeReadyForCheckIn({ bookingId, status }: UpdateBookingHomeReadyForCheckIn) {
    return arriereBackoffice.mutations.booking({
      __alias: 'updateBookingHomeReadyForCheckIn',
      updateHomeReadyForCheckInStatus: {
        __args: {
          bookingId,
          status,
        },
      },
    })
  },

  async getWholesalePaymentOptions(): Promise<string[]> {
    return arriereBackoffice.queries.wholesalePaymentOptions({})
  },

  async getAdjustmentReasons() {
    return arriereBackoffice.queries.adjustmentReasons({
      __alias: 'getAdjustmentReasons',
      negativeAdjustment: {
        name: true,
        subitem: true,
      },
      positiveAdjustment: {
        name: true,
        subitem: true,
      },
    })
  },

  async getRefundReasons() {
    return arriereBackoffice.queries.refundReasons({
      __alias: 'getRefundReasons',
      __args: {},
      name: true,
      subitem: true,
      stayCycleTags: true,
    })
  },

  async marketingInfo(bookingId: string) {
    return arrierePublic.queries.bookingMarketingInfo({
      __alias: 'getMarketingInfo',
      __args: {
        bookingId,
      },
      adults: true,
      children: true,
      infants: true,
      notes: true,
      purposeOfStay: true,
      trafficSource: true,
    })
  },

  async getIdRequestsWithPersona({
    checkInAfter,
    checkInBefore,
    filter,
    status,
    pagination,
  }: {
    checkInAfter?: string
    checkInBefore?: string
    filter?: string
    status?: Status[]
    pagination?: PaginationInput
  }) {
    return arriereBackoffice.queries.bookingIdVerifications({
      __alias: 'getIdRequestsWithPersona',
      __args: {
        checkInAfter,
        checkInBefore,
        filter,
        status,
        pagination,
      },
      total: true,
      pages: true,
      paginationParams: {
        page: true,
        size: true,
      },
      results: {
        bookingHash: true,
        bookingCreatedAt: true,
        idVerificationStatus: true,
        idVerificationCreatedAt: true,
        mappedRisks: true,
        booking: {
          id: true,
          hash: true,
          confirmationId: true,
          guestName: true,
          guests: true,
          tier: true,
          period: {
            start: true,
            end: true,
          },
          address: {
            address: true,
            state: {
              name: true,
            },
            region: {
              name: true,
            },
            zipCode: true,
          },
          home: {
            name: true,
            region: {
              name: true,
            },
          },
          folio: {
            summary: {
              value: true,
              total: true,
              isPaid: true,
            },
          },
        },
      },
    })
  },

  async getReservationTypes() {
    return arriereBackoffice.queries.customFields({
      __alias: 'getReservationTypes',
      __args: {
        // Booking Reservation Types Custom Field Id
        customFieldSectionId: 'ebe53981-1aa2-425e-a3c5-9de6ad76cc3c',
      },
      id: true,
      name: true,
      defaultValue: true,
      values: {
        value: true,
      },
    })
  },

  async getBookingConflicts({
    bookingConflictSources,
    resolvedOnly,
    unResolvedOnly,
    pagination,
  }: GetBookingConflictsParams) {
    return arriereBackoffice.queries
      .bookingResolutions({
        __alias: 'getBookingConflicts',
        listConflicts: {
          __args: {
            bookingConflictSources,
            conflictsWithBookingsOnly: false,
            resolvedOnly,
            unResolvedOnly,
            pagination,
          },
          pages: true,
          total: true,
          results: {
            bookingId: true,
            bookingBlockId: true,
            createdAt: true,
            booking: {
              hash: true,
              createdAt: true,
              home: {
                id: true,
                name: true,
                region: {
                  name: true,
                },
              },
              source: true,
              guestName: true,
              period: {
                start: true,
                end: true,
              },
            },
            conflictBlock: {
              blockId: true,
              period: {
                start: true,
                end: true,
              },
              source: true,
              bookingId: true,
            },
            conflictBlockBooking: {
              hash: true,
              createdAt: true,
              home: {
                id: true,
                name: true,
                region: {
                  name: true,
                },
              },
              source: true,
              guestName: true,
              period: {
                start: true,
                end: true,
              },
            },
            id: true,
            homeId: true,
            notes: {
              note: true,
            },
            resolved: true,
            resolvedAt: true,
            updatedAt: true,
          },
        },
      })
      .then(bookingConflict => bookingConflict.listConflicts)
  },

  async getBookingReviews(bookingId: UUID) {
    return arriereBackoffice.queries
      .booking({
        __alias: 'getBookingReviews',
        __args: {
          bookingId,
        },
        reviews: {
          __args: {
            onlyActive: undefined,
          },
          id: true,
          source: true,
          privateFeedback: true,
          response: true,
          externalReviewId: true,
          createdAt: true,
          categoryReviews: {
            category: true,
            rating: true,
            comment: true,
            tags: true,
          },
          booking: {
            guestName: true,
            guestId: true,
            hash: true,
          },
          bookingPeriod: {
            start: true,
            end: true,
          },
        },
      })
      .then(({ reviews }) => reviews)
  },

  async getBookingMessages({
    bookingHash,
    searchTerm,
    sortOrder,
    status,
    templateType,
  }: GetBookingMessagesProps) {
    return arriereBackoffice.queries
      .bookings({
        __alias: 'getBookingMessages',
        __args: {
          filter: bookingHash,
        },
        results: {
          messagesSent: {
            __args: {
              query: searchTerm,
              sortOrder,
              status,
              templateKind: templateType,
            },
            id: true,
            additionalData: true,
            createdAt: true,
            emails: true,
            message: true,
            phones: true,
            person: {
              fullName: true,
            },
            status: true,
            subject: true,
            templateKind: true,
            templateName: true,
            textMessage: true,
          },
        },
      })
      .then(({ results }) => results[0].messagesSent || [])
  },

  async getBookingReviewScheduleDate({ bookingId }: { bookingId: string }) {
    return arriereBackoffice.queries
      .booking({
        __alias: 'getBookingReviewScheduleDate',
        __args: { bookingId },
        autoRateGuestInfo: { schedule: { date: true } },
      })
      .then(data => data.autoRateGuestInfo.schedule?.date)
  },

  // Mutations

  async updateBooking({ bookingId, fieldValues }: IUpdateBookingParams) {
    return arriereBackoffice.mutations.updateBookingCustomFieldValues({
      __args: {
        bookingId,
        fieldValues,
      },
    })
  },

  async ignoreContractValidation(bookingId: string) {
    return arriereBackoffice.mutations.booking({
      __alias: 'ignoreContractValidation',
      ignoreContractValidation: {
        __args: { bookingId },
      },
    })
  },

  async requireContractValidation(bookingId: string) {
    return arriereBackoffice.mutations.booking({
      __alias: 'requireContractValidation',
      requireContractValidation: {
        __args: { bookingId },
      },
    })
  },

  async refundBookingCharges({
    paymentsToRefund,
    reason,
  }: {
    paymentsToRefund: Array<{
      paymentId: string
      amount: number
    }>
    reason: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        refundBookingCharges: {
          __args: {
            refunds: paymentsToRefund,
            reason,
          },
        },
      })
      .then(result => result.refundBookingCharges)
  },

  async refundPaymentSource(bookingId: string, methods: RefundMethodView[]) {
    return arriereBackoffice.mutations
      .payments({
        refundPaymentSource: {
          __args: {
            bookingId,
            methods,
          },
        },
      })
      .then(result => result?.refundPaymentSource)
  },

  /**
   * Returns the client_secret string to authorize payment with Stripe
   */
  async makeNewCardCharge({
    bookingId,
    amount,
    title,
  }: {
    bookingId: string
    amount: number
    title: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        makeNewCardCharge: {
          __args: {
            bookingId,
            amount,
            title,
          },
        },
      })
      .then(result => result.makeNewCardCharge)
  },

  async makeBookingCardCharge({
    bookingId,
    amount,
    title,
    cardId,
  }: {
    bookingId: string
    amount: number
    title: string
    cardId: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        makeBookingCardCharge: {
          __args: {
            bookingId,
            amount,
            title,
            cardId,
          },
        },
      })
      .then(result => result.makeBookingCardCharge)
  },

  async makePaymentLinkCharge({
    bookingId,
    amount,
    title,
    email,
  }: {
    bookingId: string
    amount: number
    title: string
    email: string
  }) {
    return arriereBackoffice.mutations.payments({
      makePaymentLinkCharge: {
        __args: {
          bookingId,
          amount,
          title,
          email,
        },
      },
    })
  },

  async makeSecurityDepositCharge({
    bookingId,
    amount,
    title,
  }: {
    bookingId: string
    amount: number
    title: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        makeSecurityDepositCharge: {
          __args: {
            amount,
            bookingId,
            title,
          },
        },
      })
      .then(result => result.makeSecurityDepositCharge)
  },

  async makeWholesaleCharge({
    bookingId,
    amount,
    title,
    platform,
    time,
  }: {
    bookingId: string
    amount: number
    title: string
    platform: string
    time: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        makeWholesaleCharge: {
          __args: {
            bookingId,
            amount,
            title,
            platform,
            time,
          },
        },
      })
      .then(result => result.makeWholesaleCharge)
  },

  async makeAchBankCharge({
    bookingId,
    amount,
    title,
    customerId,
  }: {
    bookingId: string
    amount: number
    title: string
    customerId: string
  }) {
    return arriereBackoffice.mutations
      .payments({
        makeBookingBankCharge: {
          __args: {
            bookingId,
            amount,
            title,
            customerId,
          },
        },
      })
      .then(result => result.makeBookingBankCharge)
  },

  async deleteBookingCharge({ paymentId }: { paymentId: string }) {
    return arriereBackoffice.mutations
      .payments({
        deleteBookingCharge: {
          __args: { paymentId },
        },
      })
      .then(result => result.deleteBookingCharge)
  },

  async createDocumentMutation({ description, documentKind, extension, size }: DocumentRequest) {
    return arriereBackoffice.mutations
      .booking({
        createDocument: {
          __args: {
            documentRequest: {
              description,
              documentKind,
              extension,
              size,
            },
          },
          documentId: true,
          url: true,
        },
      })
      .then(result => result.createDocument)
  },

  async approveIdWithPersona(bookingHash: string) {
    return arriereBackoffice.mutations.booking({
      approveId: {
        __args: { bookingHash },
      },
    })
  },

  async rejectIdWithPersona(bookingHash: string) {
    return arriereBackoffice.mutations.booking({
      rejectId: {
        __args: { bookingHash },
      },
    })
  },

  async requireIdVerification(bookingHash: string) {
    return arriereBackoffice.mutations.booking({
      __alias: 'requireIdVerification',
      requireIdVerification: {
        __args: { bookingHash },
      },
    })
  },
  async inPersonIdVerification(bookingHash: string) {
    return arriereBackoffice.mutations.booking({
      __alias: 'inPersonIdVerification',
      inPersonIdVerification: {
        __args: { bookingHash },
      },
    })
  },

  async skipIdVerificationWithPersona(bookingHash: string) {
    return arriereBackoffice.mutations.booking({
      skipIdVerification: {
        __args: { bookingHash },
      },
    })
  },

  async requestNewIdWithPersona(bookingId: string) {
    return arriereBackoffice.mutations.resendDocumentRequest({
      __args: { bookingId },
    })
  },

  async addNameToBlackList(name: string) {
    return arriereBackoffice.mutations.addNameToBlacklist({
      __args: { name },
    })
  },

  async addPhoneToBlackList(phone: string) {
    return arriereBackoffice.mutations.addPhoneToBlacklist({
      __args: { phone },
    })
  },

  async addEmailToBlackList(email: string) {
    return arriereBackoffice.mutations.addToBlacklist({
      __args: { email },
    })
  },

  async resendDocumentRequestEmail(bookingId: string) {
    return arriereBackoffice.mutations.resendDocumentRequest({
      __args: { bookingId },
    })
  },

  async resendCheckInInstructionsEmail(id: string) {
    return arriereBackoffice.mutations.resendCheckInInstructions({
      __args: { bookingId: id },
    })
  },

  async pushChangesToZendesk(bookingId: string): Promise<{ zendeskPushChanges: boolean }> {
    return arriereBackoffice.mutations.booking({
      __alias: 'pushChangesToZendesk',
      zendeskPushChanges: {
        __args: { bookingId },
      },
    })
  },

  async getAccountingAuditStatuses(bookingConnection: BookingConnection) {
    return arriereBackoffice.queries.accountingAuditStatuses({
      __alias: 'getAccountingAuditStatuses',
      __args: { bookingConnection },
      value: true,
      setBy: true,
      setAt: true,
    })
  },

  async getAccountingResolutionCenterData({
    args: { filterParams, pagination },
    aliasPrefix = '',
  }: {
    args: { filterParams: BookingAccountingResolutionCenterFilterParams; pagination: Pagination }
    aliasPrefix?: string
  }) {
    const {
      errorKind,
      transactionType,
      dateRange,
      byKeyword,
      bookingSources,
      hasIssues,
      status,
      paymentKind,
      origin,
      isReimburse,
      contextStatus,
    } = filterParams

    return arriereBackoffice.queries.accountingResolutionCenter({
      __alias: `${aliasPrefix ? `${aliasPrefix}_` : ''}accountingResolutionCenter`,
      __args: {
        byKeyword,
        dateRange,
        errorKind,
        transactionType,
        bookingSources: bookingSources as BookingLeadSource[] | undefined,
        pagination,
        hasIssues,
        status,
        paymentKind,
        origin,
        isReimburse,
        contextStatus,
      },
      arc: {
        issues: {
          total: true,
          pages: true,
          paginationParams: {
            page: true,
            size: true,
          },
          results: {
            checkIn: true,
            checkOut: true,
            paymentKind: true,
            source: true,
            amount: true,
            voyageAmount: true,
            confirmationId: true,
            crossReferenceCode: true,
            guestName: true,
            hash: true,
            homeName: true,
            kind: true,
            links: {
              netsuite: {
                salesOrder: true,
              },
              stripe: {
                charge: true,
                searchByBookingHash: true,
                searchByConfirmationId: true,
              },
              voyage: {
                transactions: true,
                timeline: true,
              },
            },
            message: true,
            origin: true,
            originalBalanceTransactionKind: true,
            period: {
              end: true,
              start: true,
            },
            regionName: true,
            reservationId: true,
            status: true,
            transactionDate: true,
            netsuitePayoutId: true,
            netsuitePayoutUrl: true,
            payoutRemoteId: true,
            expensyaReportUrl: true,
            username: true,
            userEmail: true,
            expensyaDate: true,
            isReimburse: true,
            expenseId: true,
            expenseNumber: true,
            memo: true,
            contextStatus: true,
            expenseStatus: true,
            date: true,
            caseNumber: true,
            bankId: true,
            bankName: true,
            bankReference: true,
            customerReference: true,
            accountNumber: true,
            accountName: true,
            description: true,
            transactionReceivedAt: true,
            id: true,
            payoutOrigin: true,
            netsuiteTransactionRef: true,
          },
        },
        issuesInformation: {
          count: {
            bookings: true,
            payments: true,
            refunds: true,
            invoices: true,
            paymentsAndRefunds: true,
            expenses: true,
            payouts: true,
          },
          originalBtrKinds: true,
          origins: true,
          statusCount: true,
          contextStatusCount: true,
          statuses: true,
        },
      },
    })
  },
  async accountingTimelineForBooking(bookingId: string) {
    return arriereBackoffice.queries.accountingTimelineForBooking({
      __alias: 'accountingTimelineForBooking',
      __args: {
        bookingId,
      },
      customers: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            customer: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            location: true,
            salesOrder: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
      customerDeposits: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            salesOrder: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            customer: true,
            location: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
      customerPayments: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            salesOrder: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            customer: true,
            location: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
      customerRefunds: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            salesOrder: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            customer: true,
            location: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
      locations: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            customer: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            location: true,
            salesOrder: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
      salesOrders: {
        connection: true,
        status: true,
        createdAt: true,
        isResolution: true,
        kind: true,
        links: {
          netsuite: {
            salesOrder: true,
            customerDepositOrPayment: true,
            customerRefund: true,
            customer: true,
            location: true,
          },
          stripe: {
            charge: true,
            searchByBookingHash: true,
            searchByConfirmationId: true,
          },
          voyage: {
            transactions: true,
            timeline: true,
          },
        },
        message: true,
        amount: true,
        transactionDate: true,
        operationKind: true,
        originalBtrKind: true,
        origin: true,
      },
    })
  },

  async getAccountingTimeline(bookingId: string) {
    return arriereBackoffice.queries
      .accountingTimeline({
        __alias: 'accountingTimeline',
        __args: {
          bookingId,
        },
        results: {
          issueType: true,
          transactionDate: true,
          amount: true,
          originalBalanceTransactionKind: true,
          origin: true,
          status: true,
          transactionType: true,
          links: {
            netsuite: { salesOrder: true },
            voyage: { transactions: true, timeline: true },
          },
          bookingId: true,
          transactionId: true,
          bookingHash: true,
          balanceTransactionCreatedAt: true,
          balanceTransactionId: true,
        },
      })
      .then(({ results }) => results)
  },
  async triggerTransactionNetSuiteSync({ transactionId }: { transactionId: string }) {
    return arriereBackoffice.mutations.accountingTimeLine
      .raw({
        __alias: 'reSyncTransaction',
        reSyncTransaction: {
          __args: {
            transactionId,
          },
        },
      })
      .then(({ data }) => data?.reSyncTransaction)
  },

  async triggerBookingNetSuiteSync({ bookingId }: { bookingId: string }) {
    return arriereBackoffice.mutations.accountingTimeLine.raw({
      __alias: 'reSyncBooking',
      reSyncBooking: {
        __args: {
          bookingId,
        },
      },
    })
  },

  async cancelBooking({ bookingId, reason, note, stayCycle }: CancelReservationActionProps) {
    return arriereBackoffice.mutations.cancelBooking.raw({
      __args: {
        bookingId,
        reason,
        note,
        stayCycle,
      },
    })
  },

  async resendMessage({
    personNotifyLogIds,
    phones,
    emails,
  }: {
    personNotifyLogIds: UUID[]
    phones?: string[]
    emails?: string[]
  }) {
    return arriereBackoffice.mutations.resendMessage({
      __args: {
        personNotifyLogIds,
        phones,
        emails,
      },
    })
  },

  async updateBookingConflictNotes({ bookingConflictId, notes }: UpdateBookingConflictNote) {
    return arriereBackoffice.mutations.bookingResolutions({
      updateNotes: {
        __args: {
          id: bookingConflictId,
          notes,
        },
        notes: {
          note: true,
        },
      },
    })
  },

  async alterBooking({
    bookingId,
    homeId,
    newRange,
    nightPrices,
    taxExemption,
    newFees,
  }: AlterBookingVariables) {
    return arriereBackoffice.mutations.booking
      .raw({
        __alias: 'alterBooking',
        alterBookingV2: {
          __args: {
            bookingId,
            homeId,
            newRange,
            nightPrices,
            taxExemption,
            newFees,
          },
          success: true,
          warnings: { message: true, code: true },
        },
      })
      .then(({ data, errors }) => ({
        data: data?.alterBookingV2.success,
        errors,
        warnings: data?.alterBookingV2.warnings,
      }))
  },

  async bookingFloorPrice(
    bookingId: UUID,
    newRange: { start: string; end: string },
    newHomeId?: UUID,
  ) {
    return arriereBackoffice.queries.bookingFloorPrice.memo({
      __alias: 'getBookingFloorPrice',
      __args: {
        bookingId,
        newRange,
        newHomeId,
      },
      date: true,
      floorPrice: true,
      regularPrice: true,
      orphanPrice: true,
    })
  },

  async checkConflicts() {
    return arriereBackoffice.mutations.bookingResolutions({
      checkConflicts: {
        newConflictsCount: true,
        unchangedConflictsCount: true,
        resolvedConflictsCount: true,
      },
    })
  },

  async reservationTypes() {
    return arriereBackoffice.queries.reservationTypes({
      id: true,
      name: true,
      isOwnerRelated: true,
      isRevenueGenerating: true,
    })
  },

  async alterDetails(values: AlterBookingDetailsMutation) {
    return arriereBackoffice.mutations.booking.raw({
      __alias: 'alterBookingDetails',
      alterDetails: {
        __args: {
          ...values,
        },
      },
    })
  },

  async updateCheckInInstructionsNotes(bookingId: string, notes?: string) {
    await arriereBackoffice.mutations.updateBookingCheckInNote({
      __alias: 'updateBookingCheckInNote',
      __args: { bookingId, note: notes },
    })
  },

  async alterBookingMainGuest(bookingId: string, newMainGuestId: string) {
    return arriereBackoffice.mutations.booking.raw({
      __alias: 'alterBookingMainGuest',
      alterMainGuest: {
        __args: {
          bookingId,
          newMainGuestId,
        },
      },
    })
  },
}

export const bookingModificationPossibility = async (bookingId: string) => {
  return arriereBackoffice.queries.bookingModificationPossibility
    .memoRaw({
      __alias: 'getBookingModificationPossibility',
      __args: {
        bookingId,
      },
      cancellationImpossibilityReason: true,
      cancellationPossible: true,
      modificationHotelRoomPossible: true,
      modificationHotelRoomImpossibilityReason: true,
      modificationImpossibilityReason: true,
      taxFeeOverrideImpossibilityReason: true,
      taxFeeOverridePossible: true,
      modificationPossibilities: {
        possible: true,
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1].message)
      }

      return data
    })
}

export const getBookingHomeInfo = async ({ bookingId, hash, confirmationId }: BookingInfoArgs) => {
  return arriereBackoffice.queries.booking
    .raw({
      __alias: 'getBookingHomeInfo',
      __args: {
        bookingId,
        hash,
        confirmationId,
      },
      home: {
        address: {
          address: true,
        },
        customFields: {
          __args: { tags: undefined },
          fields: {
            id: true,
            name: true,
            value: true,
          },
          section: {
            id: true,
          },
        },
        econPropertyFinancialHealth: true,
        hotelId: true,
        id: true,
        lifecycle: {
          listingStatus: true,
          riskLevel: true,
        },
        links: {
          listings: {
            kind: true,
            leadSource: true,
            url: true,
          },
          manual: true,
        },
        maxOccupancy: true,
        maxPets: true,
        name: true,
        operationReadinessStatus: {
          cleanDetails: {
            status: true,
          },
          inspectedDetails: {
            status: true,
          },
          status: true,
        },
        region: {
          id: true,
          name: true,
          timezone: true,
        },
        revenueModel: true,
        thumbnail: {
          url: true,
        },
        title: true,
        unitNumber: true,
      },
    })
    .then(({ data, errors }) => {
      if (!data && errors.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data.home
    })
}

export const getBookingInfoById = async ({
  bookingId,
  hash,
  confirmationId,
  tags,
  alias,
}: BookingInfoArgs & { alias?: string }) => {
  return arriereBackoffice.queries.booking
    .raw({
      __alias: alias ?? 'getBookingInfoById',
      __args: {
        bookingId,
        hash,
        confirmationId,
      },
      aboutUsDetails: true,
      acquisition: {
        isAcquisition: true,
        partner: true,
        partnerBookingId: true,
        importedOn: true,
        acquisitionType: true,
        acquisitionPhase: true,
      },
      address: {
        regionId: true,
        address: true,
        city: true,
        zipCode: true,
      },
      appDownloads: true,
      attachments: {
        name: true,
        url: true,
      },
      bookingGroupName: true,
      cancellationInfo: {
        reason: true,
        source: true,
        stayCycle: true,
        note: true,
        author: {
          kind: true,
          name: true,
        },
        createDate: true,
        appliedCancellationPolicy: {
          cancellationPolicyId: true,
        },
      },
      checkInType: true,
      confirmationId: true,
      groupBooking: { id: true, name: true },
      contracts: {
        id: true,
        bookingId: true,
        detailsUrl: true,
        additionalData: true,
        signingUrl: true,
        filesUrl: true,
        rentalAgreement: true,
        status: true,
        createdAt: true,
      },
      couponCode: true,
      createdAt: true,
      customFields: {
        __args: { tags },
        section: {
          id: true,
          name: true,
          readOnly: true,
        },
        fields: {
          id: true,
          kind: true,
          value: true,
          name: true,
          options: true,
          restriction: true,
          assets: {
            id: true,
            fileName: true,
            size: true,
            createdAt: true,
            url: true,
          },
          userAllowedActions: true,
          requiredPrivileges: {
            name: true,
            category: true,
            action: true,
          },
        },
      },
      detailedCheckIn: {
        custom: { status: true, time: true },
        effectiveTime: true,
        originalTime: true,
      },
      detailedCheckOut: {
        custom: { status: true, time: true },
        effectiveTime: true,
        originalTime: true,
      },
      emails: true,
      folio: {
        summary: {
          valueNightsAndFees: true,
          total: true,
          balance: true,
          isPaid: true,
          paymentBalanceStatus: true,
          paid: {
            total: true,
            credits: true,
          },
          value: true,
          deposit: {
            balance: true,
          },
        },
      },
      guestId: true,
      guestList: {
        email: true,
        name: true,
        phone: true,
        roomId: true,
        personId: true,
        credits: true,
        tags: {
          tag: {
            id: true,
            name: true,
            vipLevel: true,
          },
        },
      },
      guestName: true,
      guests: true,
      hash: true,
      heardAboutUs: true,
      homeId: true,
      home: {
        operationReadinessStatus: {
          status: true,
          cleanDetails: {
            status: true,
          },
          inspectedDetails: {
            status: true,
          },
        },
        region: {
          id: true,
          name: true,
          timezone: true,
        },
        unitNumber: true,
      },
      hotel: {
        id: true,
        name: true,
      },
      hotelRoomType: {
        hotelId: true,
        roomKind: true,
        id: true,
        title: true,
      },
      id: true,
      idVerificationState: {
        required: true,
        passed: true,
      },
      isFromHotel: true,
      isRoomAssigned: true,
      issues: true,
      links: {
        manual: true,
        reservationHub: true,
        zendeskLink: true,
      },
      lockCode: {
        code: true,
      },
      lockStatus: {
        name: true,
        connectivity: true,
        offlineSince: true,
        updatedAt: true,
        batteryLevel: true,
        batteryStatus: true,
      },
      marketingInfo: {
        adults: true,
        children: true,
        infants: true,
        purposeOfStay: true,
        trafficSource: true,
        notes: true,
      },
      otaBookingInfo: {
        ota: true,
        otaBookingId: true,
        channel: true,
        channelBookingId: true,
        channelAccount: {
          channelId: true,
          name: true,
        },
        listingPlatform: true,
      },
      paymentPlan: {
        createdAt: true,
        createdBy: {
          name: true,
        },
        updatedAt: true,
        updatedBy: {
          name: true,
        },
        value: true,
      },
      period: {
        start: true,
        end: true,
      },
      periodChanges: true,
      pets: true,
      phones: true,
      protection: {
        status: true,
        orderId: true,
        price: true,
        time: true,
      },
      relocations: true,
      reservationId: true,
      reservationType: {
        id: true,
        name: true,
        externalName: true,
      },
      source: true,
      status: true,
      tags: {
        bookingTags: {
          short: true,
          full: {
            reason: true,
            tag: {
              name: true,
              description: true,
              hierarchy: true,
            },
          },
        },
        guestsTags: {
          short: true,
          full: {
            reason: true,
            tag: {
              name: true,
              description: true,
              hierarchy: true,
            },
          },
        },
      },
      updatedAt: true,
      userId: true,
    })
    .then(async ({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      const home = await getBookingHomeInfo({ bookingId, hash, confirmationId })

      return { ...data, home }
    })
}

export const getBookingAccessMethods = async (bookingId: string) => {
  return arriereBackoffice.queries.bookingAccessMethods({
    __alias: 'getBookingAccessMethods',
    __args: { bookingId },
    lockCodes: {
      code: true,
      expiration: true,
      id: true,
      lastChangeAt: true,
      validFrom: true,
      home: {
        id: true,
        name: true,
        unitNumber: true,
      },
    },
    keyCards: {
      homeId: true,
      error: true,
      items: {
        id: true,
        encoded: true,
      },
    },
  })
}

export const alterationPreviewSelection = {
  taxes: {
    id: true,
    name: true,
    amount: true,
  },
  fees: {
    id: true,
    name: true,
    amount: true,
    feeCalculationType: true,
  },
  nights: {
    date: true,
    price: true,
  },
  feesBreakdown: {
    actual: {
      id: true,
      itemId: true,
      name: true,
      amount: true,
      feeCalculationType: true,
    },
    modified: {
      id: true,
      itemId: true,
      amountChange: {
        from: true,
        to: true,
      },
      feeCalculationType: true,
      name: true,
    },
    added: {
      id: true,
      itemId: true,
      name: true,
      amount: true,
      feeCalculationType: true,
    },
    nonModified: {
      id: true,
      itemId: true,
      name: true,
      amount: true,
      feeCalculationType: true,
    },
  },
  items: {
    id: true,
    kind: true,
    title: true,
    total: true,
    date: true,
    details: {
      serviceRequestId: true,
      date: true,
      dateTime: true,
      time: true,
      dateRange: {
        start: true,
        end: true,
      },
      quantity: true,
      items: true,
    },
    adjustment: {
      amount: true,
      reason: true,
    },
    cancellation: {
      reason: true,
      time: true,
    },
    createdAt: true,
    balance: true,
  },
  newPrice: true,
  newSummary: {
    valueNightsAndFees: true,
    balance: true,
    deposit: {
      balance: true,
    },
    isPaid: true,
    paid: {
      credits: true,
      total: true,
    },
    paymentBalanceStatus: true,
    total: true,
    value: true,
  },
}

export const previewAlteration = async ({
  bookingId,
  feeOverrides,
  newHomeId,
  newRange,
  nightPrices = [],
  taxExemption,
  queryAlias,
}: Partial<PreviewAlterDatesVariables>) => {
  return arriereBackoffice.queries.booking.raw({
    __alias: queryAlias || 'previewAlteration',
    __args: {
      bookingId,
    },
    previewAlteration: {
      __args: {
        nightPrices,
        feeOverrides,
        newHomeId,
        newRange,
        taxExemption,
      },
      ...alterationPreviewSelection,
    },
  })
}

const sharedPetCountAlterationFragment = {
  feesBreakdown: {
    added: {
      id: true,
      name: true,
      amount: true,
    },
    modified: {
      id: true,
      amountChange: {
        from: true,
        to: true,
      },
      name: true,
    },
  },
  fees: {
    amount: true,
    id: true,
    name: true,
  },
  newSummary: {
    total: true,
    valueNightsAndFees: true,
  },
  nights: {
    date: true,
    price: true,
  },
  taxesBreakdown: {
    added: {
      amount: true,
      id: true,
      name: true,
    },
    modified: {
      id: true,
      amountChange: {
        from: true,
        to: true,
      },
      name: true,
    },
  },
}

const airbnbPreviewAlterationFragment = {
  oldOtaGuestSummaryView: {
    total: true,
    otaGuestFees: true,
  },
  newOtaGuestSummaryView: {
    total: true,
    otaGuestFees: true,
  },
}

const airbnbPriceQuoteFragment = {
  airbnbCollectedTaxAmountAccurate: true,
  guestFeeBaseAccurate: true,
  guestFeeVatAccurate: true,
  hostFeeBaseAccurate: true,
  hostFeeVatAccurate: true,
  standardFeesDetails: {
    feeType: true,
    amountNative: true,
  },
}

export const previewPetCountAlterationQuote = async ({
  bookingId,
  pets,
}: PreviewPetCountAlterationQuoteVariables) => {
  const previewAlteration = await arriereBackoffice.queries.booking.raw({
    __alias: 'previewPetCountAlterationQuote',
    __args: {
      bookingId,
    },
    previewAlteration: {
      __args: {
        nightPrices: [],
        pets,
      },
      ...sharedPetCountAlterationFragment,
      ...airbnbPreviewAlterationFragment,
    },
  })

  return previewAlteration
}

export const previewAirbnbPetCountAlterationQuote = async ({
  bookingId,
  pets,
}: PreviewPetCountAlterationQuoteVariables) => {
  const previewAlteration = await arriereBackoffice.queries.booking.raw({
    __alias: 'previewPetCountAlterationQuote',
    __args: {
      bookingId,
    },
    homeId: true,
    otaBookingInfo: {
      otaBookingId: true,
    },
    previewAlteration: {
      __args: {
        nightPrices: [],
        pets,
        useOtaPreviewAlteration: true,
      },
      ...sharedPetCountAlterationFragment,
      ...airbnbPreviewAlterationFragment,
    },
  })

  const airbnbPriceQuote = await arriereBackoffice.queries
    .airbnbDistribution({
      __alias: 'previewExternalPetCountAlterationQuote',
      reservationAlterations: {
        getPriceQuote: {
          __args: {
            bookingId,
            newNumberOfPets: pets,
          },
          priceQuote: airbnbPriceQuoteFragment,
        },
      },
      reservations: {
        get: {
          __args: {
            homeId: previewAlteration?.data?.homeId,
            confirmationCode: previewAlteration?.data?.otaBookingInfo?.otaBookingId ?? '',
          },
          ...airbnbPriceQuoteFragment,
        },
      },
    })
    .then(res => ({
      currentPriceQuote: res.reservations.get,
      newPriceQuote: res.reservationAlterations.getPriceQuote.priceQuote,
    }))

  const currentPetFee = airbnbPriceQuote.currentPriceQuote.standardFeesDetails?.find(
    fee => fee.feeType === AirbnbFeeType.passThroughPetFee,
  )?.amountNative

  const newPetFee = airbnbPriceQuote.newPriceQuote.standardFeesDetails?.find(
    fee => fee.feeType === AirbnbFeeType.passThroughPetFee,
  )?.amountNative

  return {
    ...previewAlteration,
    data: {
      previewAlteration: {
        ...previewAlteration.data.previewAlteration,
        currentPetFee,
        newPetFee,
        currentPriceQuote: airbnbPriceQuote.currentPriceQuote,
        newPriceQuote: airbnbPriceQuote.newPriceQuote,
      },
    },
  }
}

export const getPendingAirbnbAlterations = async ({
  bookingId,
  confirmationCode,
  homeId,
}: GetPendingAirbnbAlterationsProps) => {
  const pendingAirbnbAlterations = await arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getPendingAirbnbAlterations',
      reservationAlterations: {
        getPendingAlterationForBooking: {
          __args: {
            bookingId,
          },
          createdAt: true,
          createdBy: {
            kind: true,
            name: true,
          },
          metadata: {
            changes: {
              numberOfPets: {
                from: true,
                to: true,
              },
              numberOfAdults: {
                from: true,
                to: true,
              },
              numberOfChildren: {
                from: true,
                to: true,
              },
              numberOfInfants: {
                from: true,
                to: true,
              },
              endDate: {
                from: true,
                to: true,
              },
              listingId: {
                from: true,
                to: true,
              },
              startDate: {
                from: true,
                to: true,
              },
            },
          },
          payload: {
            initiatedBy: true,
            totalPrice: true,
            guestDetails: {
              numberOfPets: true,
              numberOfAdults: true,
              numberOfChildren: true,
              numberOfInfants: true,
            },
            endDate: true,
            startDate: true,
            homeId: true,
          },
          reservationAlterationId: true,
          status: true,
          preview: {
            ...airbnbPreviewAlterationFragment,
            ...sharedPetCountAlterationFragment,
          },
        },
      },
    })
    .then(res => {
      if (res.errors && res.errors.length > 0) {
        throw new Error(JSON.stringify(res.errors))
      }

      return res.data.reservationAlterations.getPendingAlterationForBooking
    })

  if (pendingAirbnbAlterations == null) {
    return pendingAirbnbAlterations
  }

  const airbnbTaxesAndFees = await arriereBackoffice.queries
    .airbnbDistribution({
      __alias: 'getAirbnbPriceBreakdown',
      reservationAlterations: {
        getPriceBreakdown: {
          __args: {
            reservationAlterationId: pendingAirbnbAlterations.reservationAlterationId,
          },
          priceQuote: airbnbPriceQuoteFragment,
        },
      },
      reservations: {
        get: {
          __args: {
            homeId,
            confirmationCode,
          },
          ...airbnbPriceQuoteFragment,
        },
      },
    })
    .then(res => ({
      currentPriceQuote: res.reservations.get,
      newPriceQuote: res.reservationAlterations.getPriceBreakdown.priceQuote,
    }))

  const currentPetFee = airbnbTaxesAndFees.currentPriceQuote.standardFeesDetails?.find(
    fee => fee.feeType === AirbnbFeeType.passThroughPetFee,
  )?.amountNative

  const newPetFee = airbnbTaxesAndFees.newPriceQuote.standardFeesDetails?.find(
    fee => fee.feeType === AirbnbFeeType.passThroughPetFee,
  )?.amountNative

  return {
    ...pendingAirbnbAlterations,
    ...airbnbTaxesAndFees,
    currentPetFee,
    newPetFee,
  }
}

const getRelocationHome = async (homeId: string) => {
  const { data, errors } = await arriereBackoffice.queries.home.raw({
    __alias: 'getRelocationHome',
    __args: {
      homeId,
    },
    id: true,
    name: true,
    region: {
      name: true,
    },
    thumbnail: {
      url: true,
    },
  })

  if (errors?.length) {
    throw new Error(errors[errors.length - 1]?.message)
  }

  return data
}

const getRelocationRoomType = async (hotelRoomTypeId: string) => {
  const { data, errors } = await arriereBackoffice.queries.hotelRoomType.raw({
    __alias: 'getRelocationRoomType',
    get: {
      __args: {
        hotelRoomTypeId,
      },
      hotelRoomTypeId: true,
      name: true,
      referenceUnit: {
        region: {
          name: true,
        },
      },
      thumbnail: {
        url: true,
      },
    },
  })

  if (errors?.length) {
    throw new Error(errors[errors.length - 1]?.message)
  }

  const roomType = data?.get
  if (!roomType) return undefined

  return {
    id: roomType.hotelRoomTypeId,
    name: roomType.name,
    region: roomType.referenceUnit?.region,
    thumbnail: roomType.thumbnail,
  }
}

export const getPendingAlterationRelocationHome = async ({
  homeId,
  isFromHotel,
}: {
  homeId: string
  isFromHotel?: boolean
}) => {
  return isFromHotel ? getRelocationRoomType(homeId) : getRelocationHome(homeId)
}

export const acceptGuestAirbnbAlteration = async (reservationAlterationId: string) => {
  return arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'acceptGuestAirbnbAlteration',
    reservationAlterations: {
      acceptGuestInitiatedAlteration: {
        __args: {
          reservationAlterationId,
        },
      },
    },
  })
}

export const declineGuestAirbnbAlteration = async (reservationAlterationId: string) => {
  return arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'declineGuestAirbnbAlteration',
    reservationAlterations: {
      declineGuestInitiatedAlteration: {
        __args: {
          reservationAlterationId,
        },
      },
    },
  })
}

export const cancelHostAirbnbAlteration = async (reservationAlterationId: string) => {
  return arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'cancelHostAirbnbAlteration',
    reservationAlterations: {
      cancelHostInitiatedAlteration: {
        __args: {
          reservationAlterationId,
        },
      },
    },
  })
}

export const alterAirbnbBooking = async ({
  bookingId,
  newNumberOfPets,
}: AlterAirbnbBookingProps) => {
  return arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'alterAirbnbBooking',
    reservationAlterations: {
      initiateAlterationAsHost: {
        __args: {
          bookingId,
          newNumberOfPets,
        },
      },
    },
  })
}

export const getOTAFolio = async (bookingId: string) => {
  return arriereBackoffice.queries.distribution
    .raw({
      __alias: 'getOTAFolio',
      booking: {
        __args: {
          id: bookingId,
        },
        getFolio: {
          items: {
            bookingItemId: true,
            date: true,
            feeId: true,
            kind: true,
            taxId: true,
            title: true,
            total: true,
          },
          appliedDiscounts: {
            bookingItemId: true,
            date: true,
            feeId: true,
            kind: true,
            taxId: true,
            title: true,
            total: true,
          },
          summary: {
            hostFee: true,
            hostFeeBase: true,
            hostFeeVat: true,
            payout: true,
            salesOrder: true,
            subTotal: true,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1].message)
      }

      return data.booking.getFolio
    })
}

export const getBookingBlendedDiscounts = async (bookingId: string) => {
  return arriereBackoffice.queries
    .booking({
      __alias: 'getBookingBlendedDiscounts',
      __args: {
        bookingId,
      },
      folio: {
        blendedDiscounts: {
          discountAppliedItems: true,
          discountAppliedTotal: true,
        },
      },
    })
    .then(response => response?.folio?.blendedDiscounts ?? [])
}

export const BOOKING_ITEM_FIELDS = {
  paymentId: true,
  id: true,
  kind: true,
  title: true,
  total: true,
  date: true,
  balance: true,
  note: true,
  createdAt: true,
  discountApplied: true,
  details: {
    serviceRequestId: true,
    date: true,
    dateTime: true,
    time: true,
    dateRange: {
      start: true,
      end: true,
    },
    quantity: true,
    items: true,
  },
  adjustment: {
    amount: true,
    reason: true,
  },
  cancellation: {
    reason: true,
    time: true,
  },
  request: {
    status: true,
    zendeskTicketUrl: true,
    amounts: {
      requested: true,
      approved: true,
      refundedAt: true,
      refundSource: {
        cardLast4: true,
        credits: true,
        bankLast4: true,
        platform: true,
        affirm: true,
        manual: true,
        processType: true,
      },
    },
    statesHistory: {
      status: true,
      datetime: true,
      author: {
        name: true,
      },
      note: true,
    },
  },
}

export const getBookingItems = async (bookingId: string) => {
  return arriereBackoffice.queries
    .booking({
      __alias: 'getBookingItems',
      __args: {
        bookingId,
      },
      folio: {
        items: BOOKING_ITEM_FIELDS,
      },
    })
    .then(response => response?.folio?.items)
}

export const getBookingFolioInfo = async (bookingId: string) => {
  return arriereBackoffice.queries
    .booking({
      __alias: 'getBookingFolioInfo',
      __args: {
        bookingId,
      },
      folio: {
        summary: {
          valueNightsAndFees: true,
          total: true,
          balance: true,
          isPaid: true,
          paymentBalanceStatus: true,
          paid: {
            total: true,
            credits: true,
          },
          value: true,
          deposit: {
            balance: true,
          },
        },
        items: {
          id: true,
          kind: true,
          title: true,
          total: true,
          date: true,
          note: true,
          details: {
            serviceRequestId: true,
            date: true,
            dateTime: true,
            time: true,
            dateRange: {
              start: true,
              end: true,
            },
            quantity: true,
            items: true,
          },
          adjustment: {
            amount: true,
            reason: true,
          },
          cancellation: {
            reason: true,
            time: true,
          },
          createdAt: true,
          balance: true,
        },
      },
    })
    .then(response => response?.folio)
}

export const cancelBookingWithRefunds = async ({
  bookingId,
  reason,
  refundStayCycle,
  note,
  policyId,
  refundsBySource,
  refundsByService,
  cancellationSource,
}: CancelBookingWithRefundsProps) => {
  return arriereBackoffice.mutations.cancelBookingWithRefundsV2.raw({
    __alias: 'cancelBookingWithRefundsV2',
    __args: {
      bookingId,
      reason,
      note,
      policyId,
      refundStayCycle,
      refundsBySource,
      refundsByService,
      cancellationSource,
    },
  })
}

export const alterBookingLockedPrice = async ({
  bookingId,
  homeId,
  newRange,
  taxExemption,
}: AlterBookingBasicParams) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'alterBookingWithLockedPrice',
      alterBookingWithLockedPriceV2: {
        __args: {
          bookingId,
          homeId,
          newRange,
          taxExemption,
        },
        success: true,
        warnings: { message: true, code: true },
      },
    })
    .then(({ data, errors }) => {
      return {
        errors,
        warnings: data?.alterBookingWithLockedPriceV2.warnings,
        data: data?.alterBookingWithLockedPriceV2.success,
      }
    })
}

export const reinstateBooking = async (bookingId: string) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'reinstateBooking',
      reinstate: {
        __args: {
          bookingId,
        },
      },
    })
    .then(({ errors }) => {
      if (errors?.length) {
        throw new Error(errors[0]?.message ?? 'Failed to reinstate booking')
      }
    })
}

export const markBookingNoShow = async (bookingId: string) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'markBookingNoShow',
      markBookingNoShow: {
        __args: {
          bookingId,
        },
        success: true,
        errors: true,
      },
    })
    .then(({ data, errors }) => {
      return {
        success: data.markBookingNoShow.success,
        errors,
        validationErrors: data.markBookingNoShow.errors,
      }
    })
}

export const alterBookingStatusToCheckedIn = async (bookingId: string) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'alterBookingStatusToCheckedIn',
      alterBookingStatusToCheckedIn: {
        __args: {
          bookingId,
        },
        success: true,
        warnings: true,
      },
    })
    .then(({ data, errors }) => {
      return {
        success: data.alterBookingStatusToCheckedIn.success,
        errors,
        warnings: data.alterBookingStatusToCheckedIn.warnings,
      }
    })
}

export const alterBookingStatusToCheckedOut = async (bookingId: string) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'alterBookingStatusToCheckedOut',
      alterBookingStatusToCheckedOut: {
        __args: {
          bookingId,
        },
        success: true,
        warnings: true,
      },
    })
    .then(({ data, errors }) => {
      return {
        success: data.alterBookingStatusToCheckedOut.success,
        errors,
        warnings: data.alterBookingStatusToCheckedOut.warnings,
      }
    })
}
